import {
  cloneElement,
  useId,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
  type Ref,
} from 'react';
import { Portal } from './Portal';
import './Tooltip.css';

export type TooltipSide = 'top' | 'bottom' | 'right';

export interface TooltipProps {
  content: ReactNode;
  side?: TooltipSide;
  /** Hover open delay in ms. Defaults to 350 to avoid flicker on pass-through. */
  delayMs?: number;
  /** Single focusable/hoverable element that triggers the tooltip. */
  children: ReactElement;
}

interface Coords {
  top: number;
  left: number;
}

/**
 * Lightweight hover/focus tooltip. Renders into a Portal positioned from the
 * trigger's bounding rect (so it escapes overflow clipping) and wires
 * `aria-describedby` onto the child. Supplementary only — never essential
 * information. Pointer/keyboard only; touch users rely on the visible label.
 */
export function Tooltip({ content, side = 'top', delayMs = 350, children }: TooltipProps): JSX.Element {
  const id = useId();
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<Coords | null>(null);
  const timer = useRef<number | undefined>(undefined);
  const triggerRef = useRef<HTMLElement | null>(null);

  const position = (): void => {
    const el = triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const gap = 8;
    // 'right' floats beside the trigger (collapsed nav rail): centering top/bottom
    // tooltips on a narrow edge trigger would push them off the viewport.
    setCoords(
      side === 'right'
        ? { top: rect.top + rect.height / 2, left: rect.right + gap }
        : {
            top: side === 'top' ? rect.top - gap : rect.bottom + gap,
            left: rect.left + rect.width / 2,
          },
    );
  };

  const show = (): void => {
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => {
      position();
      setOpen(true);
    }, delayMs);
  };

  const hide = (): void => {
    window.clearTimeout(timer.current);
    setOpen(false);
  };

  const childRef = (children as unknown as { ref?: Ref<HTMLElement> }).ref;
  const childProps = children.props as { onPointerDown?: (event: React.PointerEvent<HTMLElement>) => void };

  const trigger = cloneElement(children, {
    ref: (node: HTMLElement | null) => {
      triggerRef.current = node;
      if (typeof childRef === 'function') childRef(node);
      else if (childRef && 'current' in childRef) {
        (childRef as React.MutableRefObject<HTMLElement | null>).current = node;
      }
    },
    'aria-describedby': open ? id : undefined,
    onMouseEnter: show,
    onMouseLeave: hide,
    // Só o foco de teclado abre: um clique também foca, e um controle que segue
    // focado depois de agir (o recolher da barra lateral) ficaria com a dica
    // pendurada depois que o ponteiro já saiu dali.
    onFocus: (event: React.FocusEvent<HTMLElement>) => {
      if (isKeyboardFocus(event.currentTarget)) show();
    },
    onBlur: hide,
    // Clicar é agir, não pedir a dica: e um controle que muda de lugar ao agir
    // (o recolher da barra lateral) sai de baixo do ponteiro sem `mouseleave`.
    onPointerDown: (event: React.PointerEvent<HTMLElement>) => {
      hide();
      childProps.onPointerDown?.(event);
    },
  } as Record<string, unknown>);

  return (
    <>
      {trigger}
      {open && coords && (
        <Portal>
          <span
            id={id}
            role="tooltip"
            className={`tooltip tooltip--${side}`}
            style={{ top: coords.top, left: coords.left }}
          >
            {content}
          </span>
        </Portal>
      )}
    </>
  );
}

function isKeyboardFocus(el: HTMLElement): boolean {
  try {
    return el.matches(':focus-visible');
  } catch {
    // Motor sem suporte ao seletor: o foco vale como antes.
    return true;
  }
}
